import { useCallback, useState } from "react";
import {
  listSourceDocuments,
  type DocumentOrder,
  type SourceDocumentListPage,
  type SourceDocumentSummary,
} from "../../../api/content/sourcesApi";
import { reportUiError, toUiError, type UiError } from "../../../errors/uiError";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";
import type { CollectionVersionRef } from "../../../shared/references/contentReferences";
import { labelSegmentType, type SearchFilters } from "../../search";

export interface DocumentListHandle {
  readonly firstPage: LoadState<SourceDocumentListPage>;
  readonly documents: readonly SourceDocumentSummary[];
  readonly totalDocumentCount: number;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly loadMoreError: UiError | null;
  readonly loadMore: () => void;
  readonly reload: () => void;
}

const PAGE_SIZE = 100;

/**
 * The documents of one corpus version that the search's `filters` keep, in `order`, a page at a
 * time ("Load more"), each with what is attached to it (F88) and its key's short form (F136).
 * The server filters; the list never narrows a page itself (W-02).
 */
export function useDocumentList(corpusVersion: CollectionVersionRef, filters: SearchFilters, order: DocumentOrder): DocumentListHandle {
  const { collectionId, commit } = corpusVersion;
  const { attached } = filters;
  const segmentType = labelSegmentType(filters);
  const loadFirstPage = useCallback(
    (signal: AbortSignal) =>
      listSourceDocuments({ corpusVersion: { collectionId, commit }, attached, segmentType, order, offset: 0, limit: PAGE_SIZE }, signal),
    [collectionId, commit, attached, segmentType, order],
  );
  const { state: firstPage, reload: reloadFirstPage } = useLoad(loadFirstPage);
  // Pages belong to one version, filter and order; any change starts the list again.
  const listing = `${commit} ${order} ${JSON.stringify(filters)}`;
  const [furtherPages, setFurtherPages] = useState<{ readonly listing: string; readonly documents: readonly SourceDocumentSummary[] } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<UiError | null>(null);

  const laterDocuments = furtherPages?.listing === listing ? furtherPages.documents : [];
  const documents = firstPage.status === "loaded" ? [...firstPage.value.documents, ...laterDocuments] : [];
  const totalDocumentCount = firstPage.status === "loaded" ? firstPage.value.totalDocumentCount : 0;
  const hasMore = documents.length < totalDocumentCount;

  const loadMore = useCallback(() => {
    if (!hasMore || loadingMore) {
      return;
    }
    setLoadingMore(true);
    setLoadMoreError(null);
    listSourceDocuments({ corpusVersion: { collectionId, commit }, attached, segmentType, order, offset: documents.length, limit: PAGE_SIZE }).then(
      (page) => {
        setFurtherPages((previous) => ({
          listing,
          documents: [...(previous?.listing === listing ? previous.documents : []), ...page.documents],
        }));
        setLoadingMore(false);
      },
      (caught: unknown) => {
        const error = toUiError(caught);
        reportUiError(error);
        setLoadMoreError(error);
        setLoadingMore(false);
      },
    );
  }, [collectionId, commit, attached, segmentType, order, listing, documents.length, hasMore, loadingMore]);

  const reload = useCallback(() => {
    setFurtherPages(null);
    setLoadMoreError(null);
    reloadFirstPage();
  }, [reloadFirstPage]);

  return { firstPage, documents, totalDocumentCount, hasMore, loadingMore, loadMoreError, loadMore, reload };
}
